// src/services/auth.service.js
import apiClient from './api.service';

class AuthService {
  constructor() {
    this.logoutCallbacks = [];
  }

  // Register callback to be called on logout
  onLogout(callback) {
    this.logoutCallbacks.push(callback);
  }

  // Trigger all logout callbacks
  triggerLogout() {
    this.logoutCallbacks.forEach(callback => callback());
  }

  // Decode JWT token to get expiration time
  decodeToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // Check if token is expired
  isTokenExpired(token) {
    if (!token) return true;
    
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    
    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  }

  // Get time until token expires (in milliseconds)
  getTimeUntilExpiry(token) {
    if (!token) return 0;
    
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return 0;
    
    const currentTime = Date.now() / 1000;
    const timeRemaining = (decoded.exp - currentTime) * 1000;
    return timeRemaining > 0 ? timeRemaining : 0;
  }

  async register(userData) {
    const response = await apiClient.post('/auth/register', {
      email: userData.email,
      password: userData.password,
      fullName: userData.fullName || 'User',
      promoCode: userData.promoCode
    });
    return response.data;
  }

  async login(credentials) {
    const response = await apiClient.post('/auth/login', {
      email: credentials.email,
      password: credentials.password
    });

    if (response.data.token) {
      this.setToken(response.data.token);
      if (response.data.userId) {
        localStorage.setItem('userId', response.data.userId);
      }
      if (response.data.email) {
        localStorage.setItem('userEmail', response.data.email);
      }
      if (response.data.fullName) {
        localStorage.setItem('fullName', response.data.fullName);
      }
    }

    return response.data;
  }

  async verifyOTP(email, otp) {
    const response = await apiClient.post('/auth/verify-otp', { email, otp });
    
    if (response.data.token) {
      this.setToken(response.data.token);
      if (response.data.userId) {
        localStorage.setItem('userId', response.data.userId);
      }
      if (response.data.email) {
        localStorage.setItem('userEmail', response.data.email);
      }
      if (response.data.fullName) {
        localStorage.setItem('fullName', response.data.fullName);
      }
    }
    
    return response.data;
  }

  async resendOTP(email) {
    const response = await apiClient.post('/auth/resend-otp', { email });
    return response.data;
  }

  async forgotPassword(email, resetUrl) {
    const response = await apiClient.post('/auth/forgot-password', {
      email,
      resetUrl
    });
    return response.data;
  }

  async resetPassword(token, newPassword) {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      newPassword
    });
    return response.data;
  }

  async changePassword(userId, currentPassword, newPassword) {
    const response = await apiClient.post('/auth/change-password', {
      userId,
      currentPassword,
      newPassword
    });
    return response.data;
  }

  async deleteAccount(userId) {
    const response = await apiClient.delete(`/auth/delete-account/${userId}`);
    return response.data;
  }

  async checkPromoAvailability() {
    try {
      const response = await apiClient.get('/auth/promo/current-month');
      return response.data;
    } catch (err) {
      console.error('Failed to check promo availability:', err);
      return { withinBudget: false };
    }
  }

  async getReferralCount(userId) {
    try {
      const response = await apiClient.get(`/auth/promo/check/${userId}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch referral count:', err);
      return { count: 0, promoCode: null };
    }
  }

  setToken(token) {
    localStorage.setItem('token', token);
    localStorage.setItem('tokenSetAt', Date.now().toString());
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('fullName');
    localStorage.removeItem('tokenSetAt');
    this.triggerLogout();
  }

  clearStorage() {
    // Just clear storage without triggering callbacks
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('fullName');
    localStorage.removeItem('tokenSetAt');
  }

  getToken() {
    return localStorage.getItem('token');
  }

  getUserId() {
    return localStorage.getItem('userId');
  }

  getEmail() {
    return localStorage.getItem('userEmail');
  }

  getFullName() {
    return localStorage.getItem('fullName');
  }

  getCurrentUser() {
    const token = this.getToken();
    const userId = this.getUserId();
    const email = this.getEmail();
    const fullName = this.getFullName();

    if (!token || !userId) {
      return null;
    }

    return {
      userId,
      email,
      fullName,
      token
    };
  }

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;
    
    // Check if token is expired
    if (this.isTokenExpired(token)) {
      this.clearStorage();
      return false;
    }
    
    return true;
  }
}

const authService = new AuthService();

export default authService;